import { z } from "zod";
import { DATE_RANGE_WITH_CUSTOM_VALUES } from "@/lib/date-range";
import { EXECUTION_OUTCOMES } from "./types";

export const TOOL_EXECUTIONS_DESCRIPTION =
  "Every tool run recorded for this project, newest first.";

export const TOOL_EXECUTIONS_PAGE_SIZES = [25, 50, 100] as const;

const DEFAULT_PAGE_SIZE = 25;

export const toolExecutionsSearchSchema = z.object({
  toolName: z.string().catch("").default(""),
  toolkitNames: z.array(z.string()).catch([]).default([]),
  userId: z.string().catch("").default(""),
  outcome: z.enum(EXECUTION_OUTCOMES).catch("all").default("all"),
  failureReason: z.string().catch("").default(""),
  dateRange: z.enum(DATE_RANGE_WITH_CUSTOM_VALUES).catch("all").default("all"),
  startTime: z.string().catch("").default(""),
  endTime: z.string().catch("").default(""),
  limit: z
    .number()
    .int()
    .min(1)
    .max(TOOL_EXECUTIONS_PAGE_SIZES.at(-1) ?? 100)
    .catch(DEFAULT_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  offset: z.number().int().min(0).catch(0).default(0),
  executionId: z.string().catch("").default(""),
});

export type ToolExecutionsSearch = z.infer<typeof toolExecutionsSearchSchema>;

export const toolExecutionsSearchDefaults: ToolExecutionsSearch = {
  toolName: "",
  toolkitNames: [],
  userId: "",
  outcome: "all",
  failureReason: "",
  dateRange: "all",
  startTime: "",
  endTime: "",
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
  executionId: "",
};

/** The subset of the search state that narrows the result set. */
export type ToolExecutionsFilters = Omit<
  ToolExecutionsSearch,
  "limit" | "offset" | "executionId"
>;

/**
 * Pagination and the open-panel id must stay out of the filter set: they belong
 * to different query keys, and folding them in would refetch the list every
 * time a row is opened.
 */
export const pickFilters = ({
  toolName,
  toolkitNames,
  userId,
  outcome,
  failureReason,
  dateRange,
  startTime,
  endTime,
}: ToolExecutionsSearch): ToolExecutionsFilters => ({
  toolName,
  toolkitNames,
  userId,
  outcome,
  failureReason,
  dateRange,
  startTime,
  endTime,
});

export const toolExecutionsFilterDefaults: ToolExecutionsFilters = {
  toolName: "",
  toolkitNames: [],
  userId: "",
  outcome: "all",
  failureReason: "",
  dateRange: "all",
  startTime: "",
  endTime: "",
};
